import { useRef, useState } from "react";
import Slider from "react-slick";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CardMedia,
  IconButton,
  Divider,
  Dialog,
  DialogContent,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import Skeleton from "@mui/material/Skeleton";
import { useNavigate } from "react-router-dom";
import { LocationOn } from "@mui/icons-material";

export default function GiooPlot_card({
  name,
  address,
  image,
  images = [],
  plotImage,
  totalPrice,
  totalAmount,
  price,
  id,
  created,
  nearBy,
  amenity,
  soldStatus,
  sold_status,
}) {
  const isSold = soldStatus === 1 || sold_status === 1;
  const navigate = useNavigate();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up("md"));
  const [imgOpen, setImgOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [imgPressed, setImgPressed] = useState(false);
  const previewSliderRef = useRef(null);
  const sliderRef = useRef(null);
  const videoRefs = useRef({});
  const [playingVideo, setPlayingVideo] = useState(null);
  const [hoveredVideo, setHoveredVideo] = useState(null);
  const [loadedMedia, setLoadedMedia] = useState({});

  const isVideo = (url) => {
    if (typeof url !== "string") return false;
    return /\.(mp4|webm|mkv|mov|avi)$/i.test(url);
  };

  const handleMediaLoad = (src) => {
    setLoadedMedia((prev) => ({ ...prev, [src]: true }));
  };

  const togglePlay = (e, src) => {
    e.stopPropagation();
    const video = videoRefs.current[src];
    if (video) {
      if (video.paused) {
        if (playingVideo && playingVideo !== src && videoRefs.current[playingVideo]) {
          videoRefs.current[playingVideo].pause();
        }
        video.play();
        setPlayingVideo(src);
      } else {
        video.pause();
        setPlayingVideo(null);
      }
    }
  };

  const safeNearBy = Array.isArray(nearBy) ? nearBy : [];
  const safeAmenity = Array.isArray(amenity) ? amenity : [];
  const limitToThree = (arr) => (Array.isArray(arr) ? arr.slice(0, 3) : []);
  const nearByList = limitToThree(safeNearBy);
  const amenityList = limitToThree(safeAmenity);

  // Build images list: prefer gallery images[], fall back to single image prop
  const allImages =
    Array.isArray(images) && images.length > 0
      ? images
      : image
        ? [image]
        : ["/assets/images/placeholder_bg.png"];

  const showNavArrows = allImages.length > 1;

  const handleOpenPreview = (index = 0) => {
    setActiveIndex(index);
    setImgOpen(true);
    setTimeout(() => {
      previewSliderRef.current?.slickGoTo(index);
    }, 100);
  };

  return (
    <>
      {/* MULTI-IMAGE POPUP DIALOG */}
      <Dialog
        open={imgOpen}
        onClose={() => setImgOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: "rgba(0,0,0,0.85)",
          },
        }}
      >
        <IconButton
          onClick={() => setImgOpen(false)}
          sx={{
            position: "absolute",
            top: { xs: 10, sm: 16 },
            right: { xs: 10, sm: 20 },
            zIndex: 10,
            color: "#fff",
            bgcolor: "rgba(0,0,0,0.4)",
            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              position: "relative",
              height: { xs: 300, sm: 480, md: 620 },
              overflow: "hidden",
            }}
          >
            {/* LEFT ARROW */}
            {showNavArrows && (
              <IconButton
                onClick={() => previewSliderRef.current?.slickPrev()}
                sx={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 3,
                  color: "#fff",
                  bgcolor: "rgba(0,0,0,0.5)",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>
            )}

            <Slider
              ref={previewSliderRef}
              initialSlide={activeIndex}
              beforeChange={(oldIndex, newIndex) => {
                const vid = videoRefs.current[allImages[oldIndex]];
                if (vid) vid.pause();
                setPlayingVideo(null);
                setActiveIndex(newIndex);
              }}
              dots
              infinite={false}
              speed={400}
              slidesToShow={1}
              slidesToScroll={1}
              arrows={false}
            >
              {allImages.map((img, i) => (
                <Box key={i}>
                  <Box
                    sx={{
                      height: { xs: 300, sm: 480, md: 620 },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(0,0,0,0.7)",
                      p: 1,
                      position: "relative"
                    }}
                  >
                    {!loadedMedia[img] && (
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height="100%"
                        sx={{ position: "absolute", top: 0, left: 0, bgcolor: "rgba(255,255,255,0.1)" }}
                      />
                    )}
                    {isVideo(img) ? (
                      <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                        <video
                          ref={(el) => (videoRefs.current[img] = el)}
                          src={img}
                          playsInline
                          disablePictureInPicture
                          disableRemotePlayback
                          controlsList="nodownload nopictureinpicture"
                          onLoadedData={() => handleMediaLoad(img)}
                          onClick={(e) => togglePlay(e, img)}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            display: loadedMedia[img] ? "block" : "none",
                            cursor: "pointer",
                          }}
                        />
                        {/* Standard Rectangular Play/Pause Buttons */}
                        {loadedMedia[img] && (
                          <Box
                            onClick={(e) => togglePlay(e, img)}
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              bgcolor: "rgba(0,0,0,0.5)",
                              color: "white",
                              width: 60,
                              height: 50,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              borderRadius: "4px",
                              zIndex: 2,
                            }}
                          >
                            {playingVideo === img ? (
                              <PauseIcon sx={{ fontSize: 40 }} />
                            ) : (
                              <PlayArrowIcon sx={{ fontSize: 40 }} />
                            )}
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <img
                        src={img}
                        alt={`${name || ""} ${i + 1}`}
                        onLoad={() => handleMediaLoad(img)}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          display: loadedMedia[img] ? "block" : "none",
                        }}
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </Slider>

            {/* RIGHT ARROW */}
            {showNavArrows && (
              <IconButton
                onClick={() => previewSliderRef.current?.slickNext()}
                sx={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 3,
                  color: "#fff",
                  bgcolor: "rgba(0,0,0,0.5)",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* CARD */}
      <Card
        onClick={() => navigate(`/gioo-plots/details/${id}`)}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          borderRadius: 4,
          backgroundImage: "url('/assets/images/syndicate_bg_1.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          p: { xs: 1, sm: 1.5 },
          overflow: "hidden",
          width: "100%",
          mx: "auto",
          gap: { xs: 1, sm: 2 },
          backgroundColor: "#FBFBF8",
          cursor: "pointer",
          transition: "transform 0.22s ease, box-shadow 0.22s ease",
          "&:hover": {
            transform: "translateY(-4px) scale(1.01)",
            boxShadow: "0 10px 24px rgba(0,0,0,0.13)",
          },
        }}
      >
        {/* RIGHT CONTENT */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            p: 2,
            opacity: isSold ? 0.65 : 1,
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Typography
              variant="h6"
              color="text-primary"
              sx={{
                fontWeight: { xs: 500, sm: 600, md: 500, lg: 800 },
                fontSize: { xs: "1rem", sm: "1.2rem", md: "1rem", lg: "1.3rem" },
              }}
            >
              {name || ""}
            </Typography>

            <Box display="flex" alignItems="center" mt={0.5} mb={1}>
              <LocationOn sx={{ fontSize: 18, color: "#9DC354", mr: 0.5 }} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {address || ""}
              </Typography>
            </Box>

            <Divider
              sx={{
                borderStyle: "dashed",
                borderColor: "secondary.light",
                borderWidth: 1,
                display: { xs: "block", sm: "block" },
              }}
            />

            {totalAmount && (
              <Box sx={{ mt: 1.5, mb: 0.5, px: 0.5 }}>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.05rem", sm: "1.1rem", md: "1.15rem", lg: "1.25rem" }, color: "#2E7D32", display: "flex", alignItems: "center" }}>
                  <CurrencyRupeeIcon sx={{ fontSize: { xs: 18, sm: 20, md: 22 }, mr: 0.4 }} />
                  {Number(totalAmount).toLocaleString("en-IN")}
                  <Typography component="span" sx={{ fontSize: 13, color: "text.secondary", ml: 1, fontWeight: 500 }}>Total Price</Typography>
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 0.5,
                gap: 2,
                width: "100%",
              }}
            >
              {/* PRICE PER SQFT */}
              <Box sx={{ flex: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, whiteSpace: "nowrap" }}>
                  <CurrencyRupeeIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: "#9DC354", flexShrink: 0 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: { xs: 13, sm: 14, md: 15, lg: 16 } }}>
                    {price || ""}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: 11, sm: 12, md: 13 }, color: "text.secondary" }}>
                    / Sqft
                  </Typography>
                </Box>
                <Typography sx={{ mt: 0.3, fontSize: { xs: 11, sm: 12, md: 13 }, color: "text.secondary", textAlign: "left" }}>
                  Per Sq.ft
                </Typography>
              </Box>

              {/* AREA */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, whiteSpace: "nowrap" }}>
                  <OpenWithIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: "#9DC354", flexShrink: 0 }} />
                  <Typography sx={{ fontSize: { xs: 12, sm: 14, md: 15, lg: 16 }, fontWeight: 600 }}>
                    {totalPrice || ""}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: 11, sm: 12, md: 13 }, color: "text.secondary" }}>
                    Sq.ft
                  </Typography>
                </Box>
                <Typography sx={{ mt: 0.3, fontSize: { xs: 11, sm: 12, md: 13 }, color: "text.secondary", textAlign: "left" }}>
                  Total Area
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ borderStyle: "dashed", borderColor: "secondary.light", borderWidth: 1, my: 1, display: { xs: "block", sm: "none", md: "block" } }} />

            <Box mt={1.5}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
                Near By:&nbsp;
                <Typography component="span" variant="body2" sx={{ mb: 1 }} color="text.secondary">
                  {nearByList?.length > 0
                    ? nearByList.map((item, index) => (
                      <span key={item.id}>
                        {item.title}
                        {index !== nearByList.length - 1 && " • "}
                      </span>
                    ))
                    : <span>No Data Found</span>}
                </Typography>
              </Typography>
            </Box>

            <Box mt={1.5}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
                Amenities:&nbsp;
                <Typography component="span" variant="body2" sx={{ mb: 1 }} color="text.secondary">
                  {amenityList?.length > 0
                    ? amenityList.map((item, index) => (
                      <span key={item.id}>
                        {item.title}
                        {index !== amenityList.length - 1 && " • "}
                      </span>
                    ))
                    : <span>No Data Found</span>}
                </Typography>
              </Typography>
            </Box>
          </CardContent>

          <Button
            variant="contained"
            sx={{
              textTransform: "none",
              bgcolor: "#FDBA15",
              color: "black",
              "&:hover": { bgcolor: "#e3a30f" },
              borderRadius: 20,
              fontWeight: 500,
              mt: { xs: 2, md: 1 },
            }}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/gioo-plots/details/${id}`);
            }}
          >
            View Details
          </Button>
        </Box>

        {/* LEFT IMAGE */}
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{ flexShrink: 0, width: { xs: "100%", sm: 320, md: 240, lg: 320 }, position: "relative" }}
        >
          {isSold && (
            <Box
              sx={{
                position: "absolute",
                top: 12,
                left: 12,
                bgcolor: "#d32f2f",
                color: "#fff",
                px: 2,
                py: 0.6,
                borderRadius: 1.5,
                fontSize: 14,
                fontWeight: 800,
                zIndex: 3,
                textTransform: "uppercase",
                boxShadow: "0 4px 12px rgba(0,0,0,0.45)",
                letterSpacing: "0.5px",
              }}
            >
              Sold Out
            </Box>
          )}
          <Box
            sx={{
              width: "100%",
              height: "100%",
              opacity: isSold ? 0.6 : 1,
              filter: isSold ? "grayscale(20%)" : "none",
            }}
          >
            {allImages.length > 1 ? (
            <Box
              sx={{
                width: "100%",
                height: { xs: 180, sm: 320, md: 300, lg: 330 },
                borderRadius: 3,
                overflow: "hidden",
                position: "relative",
                "& .slick-slider, & .slick-list, & .slick-track, & .slick-slide > div": {
                  height: "100% !important",
                },
              }}
            >
              <Slider 
                ref={sliderRef} 
                dots={false} 
                arrows={false} 
                infinite={false} 
                speed={500} 
                slidesToShow={1} 
                slidesToScroll={1}
                beforeChange={() => {
                  if (playingVideo) {
                    const video = videoRefs.current[playingVideo];
                    if (video) video.pause();
                    setPlayingVideo(null);
                  }
                }}
              >
                {allImages.map((img, i) => (
                  <Box key={i} sx={{ height: "100%" }}>
                    {isVideo(img) ? (
                      <Box
                        onMouseEnter={() => setHoveredVideo(img)}
                        onMouseLeave={() => setHoveredVideo(null)}
                        sx={{ position: "relative", width: "100%", height: "100%" }}
                      >
                        <video
                          ref={(el) => (videoRefs.current[img] = el)}
                          src={img}
                          playsInline
                          preload="metadata"
                          disablePictureInPicture
                          disableRemotePlayback
                          controlsList="nodownload nopictureinpicture"
                          onLoadedData={() => handleMediaLoad(img)}
                          onClick={(e) => {
                            if (playingVideo !== img) {
                              togglePlay(e, img);
                            } else {
                              handleOpenPreview(i);
                            }
                          }}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: loadedMedia[img] ? "block" : "none",
                            cursor: "pointer",
                          }}
                        />
                        {/* Standard Rectangular Play/Pause Buttons */}
                        {loadedMedia[img] && (
                          <Box
                            onClick={(e) => togglePlay(e, img)}
                            onMouseEnter={() => setHoveredVideo(img)}
                            onMouseLeave={() => setHoveredVideo(null)}
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              bgcolor: "rgba(0,0,0,0.5)",
                              color: "white",
                              width: 50,
                              height: 40,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              borderRadius: "4px",
                              opacity: playingVideo === img ? (hoveredVideo === img ? 1 : 0) : 1,
                              transition: "opacity 0.2s ease",
                              zIndex: 2,
                            }}
                          >
                            {playingVideo === img ? (
                              <PauseIcon sx={{ fontSize: 30 }} />
                            ) : (
                              <PlayArrowIcon sx={{ fontSize: 30 }} />
                            )}
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                        {!loadedMedia[img] && (
                          <Skeleton
                            variant="rectangular"
                            width="100%"
                            height="100%"
                            sx={{ position: "absolute", top: 0, left: 0 }}
                          />
                        )}
                        <CardMedia
                          component="img"
                          image={img}
                          onLoad={() => handleMediaLoad(img)}
                          onClick={(e) => { e.stopPropagation(); handleOpenPreview(i); }}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            cursor: "zoom-in",
                            display: loadedMedia[img] ? "block" : "none",
                            transition: "transform 0.18s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.18s ease",
                            "&:hover": { transform: "scale(1.04)" },
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                ))}
              </Slider>
              {/* ARROWS */}
              <IconButton
                onClick={(e) => { e.stopPropagation(); sliderRef.current?.slickPrev(); }}
                sx={{
                  position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)",
                  zIndex: 2, bgcolor: "rgba(255,255,255,0.7)", p: 0.5,
                  "&:hover": { bgcolor: "white" }, display: { xs: "none", sm: "flex" }
                }}
              >
                <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                onClick={(e) => { e.stopPropagation(); sliderRef.current?.slickNext(); }}
                sx={{
                  position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                  zIndex: 2, bgcolor: "rgba(255,255,255,0.7)", p: 0.5,
                  "&:hover": { bgcolor: "white" }, display: { xs: "none", sm: "flex" }
                }}
              >
                <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 10,
                  right: 10,
                  bgcolor: "rgba(0,0,0,0.55)",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 700,
                  borderRadius: "6px",
                  px: 1,
                  py: 0.3,
                  pointerEvents: "none",
                  zIndex: 2,
                }}
              >
                +{allImages.length - 1} more
              </Box>
            </Box>
          ) : (
            isVideo(allImages[0]) ? (
              <Box
                sx={{
                  width: "100%",
                  height: { xs: 180, sm: 320, md: 300, lg: 330 },
                  borderRadius: 3,
                  overflow: "hidden",
                  position: "relative"
                }}
              >
                {!loadedMedia[allImages[0]] && (
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="100%"
                    sx={{ position: "absolute", top: 0, left: 0 }}
                  />
                )}
                <video
                  ref={(el) => (videoRefs.current[allImages[0]] = el)}
                  src={allImages[0]}
                  playsInline
                  preload="metadata"
                  disablePictureInPicture
                  disableRemotePlayback
                  controlsList="nodownload nopictureinpicture"
                  onLoadedData={() => handleMediaLoad(allImages[0])}
                  onMouseEnter={() => setHoveredVideo(allImages[0])}
                  onMouseLeave={() => setHoveredVideo(null)}
                  onClick={(e) => {
                    if (playingVideo !== allImages[0]) {
                      togglePlay(e, allImages[0]);
                    } else {
                      handleOpenPreview(0);
                    }
                  }}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: loadedMedia[allImages[0]] ? "block" : "none",
                    cursor: "pointer",
                  }}
                />
                {/* Standard Rectangular Play/Pause Buttons */}
                {loadedMedia[allImages[0]] && (
                  <Box
                    onClick={(e) => togglePlay(e, allImages[0])}
                    onMouseEnter={() => setHoveredVideo(allImages[0])}
                    onMouseLeave={() => setHoveredVideo(null)}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      bgcolor: "rgba(0,0,0,0.5)",
                      color: "white",
                      width: 50,
                      height: 40,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      borderRadius: "4px",
                      opacity: playingVideo === allImages[0] ? (hoveredVideo === allImages[0] ? 1 : 0) : 1,
                      transition: "opacity 0.2s ease",
                      zIndex: 2,
                    }}
                  >
                    {playingVideo === allImages[0] ? (
                      <PauseIcon sx={{ fontSize: 30 }} />
                    ) : (
                      <PlayArrowIcon sx={{ fontSize: 30 }} />
                    )}
                  </Box>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: { xs: 180, sm: 320, md: 300, lg: 330 },
                  borderRadius: 3,
                  overflow: "hidden",
                  position: "relative"
                }}
              >
                {!loadedMedia[allImages[0]] && (
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="100%"
                    sx={{ position: "absolute", top: 0, left: 0 }}
                  />
                )}
                <CardMedia
                  component="img"
                  image={allImages[0]}
                  onLoad={() => handleMediaLoad(allImages[0])}
                  onClick={(e) => {
                    e.stopPropagation();
                    setImgPressed(true);
                    setTimeout(() => setImgPressed(false), 320);
                    handleOpenPreview(0);
                  }}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    cursor: "zoom-in",
                    display: loadedMedia[allImages[0]] ? "block" : "none",
                    transition: "transform 0.18s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.18s ease",
                    transform: imgPressed ? "scale(0.92)" : "scale(1)",
                    "&:hover": { transform: imgPressed ? "scale(0.92)" : "scale(1.04)" },
                  }}
                  alt="Plot"
                />
              </Box>
            )
          )}
          </Box>
        </Box>
      </Card>
    </>
  );
}